import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SignalBadge from '../components/SignalBadge';
import PriceCard from '../components/PriceCard';
import { fetchIndicators, fetchStrategies, fetchTrades } from '../api';

const REFRESH_INTERVAL = 15000;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [stratData, setStratData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [indRes, stratRes, tradeRes] = await Promise.allSettled([
        fetchIndicators(),
        fetchStrategies(),
        fetchTrades('', 10),
      ]);
      if (indRes.status === 'fulfilled' && indRes.value.data?.data) setData(indRes.value.data.data);
      if (stratRes.status === 'fulfilled' && stratRes.value.data?.strategies) setStratData(stratRes.value.data);
      if (tradeRes.status === 'fulfilled' && tradeRes.value.data?.trades) setTrades(tradeRes.value.data.trades);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {}
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const totalPnl = stratData?.strategies?.reduce((sum, s) => sum + (s.pnl || 0), 0) || 0;
  const activeCount = stratData?.strategies?.filter(s => s.state === 'ACTIVE').length || 0;
  const totalStrats = stratData?.strategies?.length || 0;

  const niftyChange = data ? data.nifty_price - (data.nifty_prev_close || 0) : 0;
  const niftyChangePct = data && data.nifty_prev_close > 0 ? ((niftyChange / data.nifty_prev_close) * 100) : 0;

  return (
    <div className="page-dashboard">
      {/* Page Header */}
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Market overview and strategy performance</p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      {/* Market Overview Cards */}
      {data && (
        <section className="dash-market-row">
          <div className="dash-card dash-card-nifty">
            <div className="dash-card-icon">📊</div>
            <div className="dash-card-body">
              <span className="dash-card-label">NIFTY 50</span>
              <span className="dash-card-value">{data.nifty_price?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`dash-card-change ${niftyChange >= 0 ? 'positive' : 'negative'}`}>
                {niftyChange >= 0 ? '▲' : '▼'} {niftyChangePct.toFixed(2)}% ({niftyChange >= 0 ? '+' : ''}{niftyChange.toFixed(0)} pts)
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-vix">
            <div className="dash-card-icon">📈</div>
            <div className="dash-card-body">
              <span className="dash-card-label">India VIX</span>
              <span className="dash-card-value">{data.vix_value?.toFixed(2)}</span>
              <span className={`dash-card-change ${(data.vix_change_pct || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(data.vix_change_pct || 0) >= 0 ? '▲' : '▼'} {Math.abs(data.vix_change_pct || 0).toFixed(2)}%
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-bias">
            <div className="dash-card-icon">🎯</div>
            <div className="dash-card-body">
              <span className="dash-card-label">Market Bias</span>
              <SignalBadge signal={data.overall_bias} />
              <span className="dash-bias-counts">
                <span className="bias-mini-b">▲ {data.bullish_count}</span>
                <span className="bias-mini-r">▼ {data.bearish_count}</span>
              </span>
            </div>
          </div>
          <div className={`dash-card dash-card-pnl ${totalPnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
            <div className="dash-card-icon">💰</div>
            <div className="dash-card-body">
              <span className="dash-card-label">Today's P&L</span>
              <span className={`dash-card-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
                {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}
              </span>
              <span className="dash-card-change neutral">{activeCount}/{totalStrats} strategies active</span>
            </div>
          </div>
        </section>
      )}

      {!data && (
        <div className="dash-loading-strip">
          <div className="spinner" /> Waiting for market data...
        </div>
      )}

      {/* Day Range Strip */}
      {data && (
        <section className="dash-range-strip">
          <div className="range-item">
            <span className="range-label">Day Low</span>
            <span className="range-val">{data.nifty_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-bar">
            <div className="range-bar-fill" style={{
              left: data.nifty_low && data.nifty_high && data.nifty_high !== data.nifty_low
                ? `${((data.nifty_price - data.nifty_low) / (data.nifty_high - data.nifty_low)) * 100}%`
                : '50%'
            }} />
          </div>
          <div className="range-item">
            <span className="range-label">Day High</span>
            <span className="range-val">{data.nifty_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-divider" />
          <div className="range-item">
            <span className="range-label">Range</span>
            <span className="range-val">{data.intraday_range?.toFixed(0)} pts</span>
          </div>
          <div className="range-item">
            <span className="range-label">Prev Close</span>
            <span className="range-val">{data.nifty_prev_close?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </section>
      )}

      {/* Strategy Cards */}
      <section className="dash-section">
        <div className="dash-section-header">
          <h2>Strategies</h2>
          <button className="dash-view-all" onClick={() => navigate('/strategies')}>View All →</button>
        </div>
        <div className="dash-strat-grid">
          {stratData?.strategies?.map((strat) => (
            <div
              key={strat.name}
              className={`dash-strat-card ${strat.state === 'ACTIVE' ? 'active-border' : ''}`}
              onClick={() => navigate(`/strategies/${strat.name}`)}
            >
              <div className="dsc-top">
                <div className="dsc-top-left">
                  <span className={`strat-state-dot strat-dot-${strat.state?.toLowerCase()}`} />
                  <span className="dsc-name">{strat.display_name || (strat.entry_type === 'opening_range' ? 'Opening Range' : 'ATM Straddle') + ' · ' + strat.index + ' · ' + strat.trading_day}</span>
                </div>
                <span className={`dsc-pnl ${(strat.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(strat.pnl || 0) >= 0 ? '+' : ''}{(strat.pnl || 0).toFixed(2)}
                </span>
              </div>
              {strat.description && <p className="dsc-desc">{strat.description}</p>}
              <div className="dsc-tags">
                <span className="dsc-tag">{strat.index}</span>
                <span className="dsc-tag">{strat.trading_day}</span>
                <span className="dsc-tag">{strat.adjustment_points}pt</span>
                <span className="dsc-tag">{strat.account_name || 'Paper'}</span>
                <span className={`dsc-state strat-state-${strat.state?.toLowerCase()}`}>{strat.state}</span>
              </div>
            </div>
          ))}
          {(!stratData?.strategies || stratData.strategies.length === 0) && (
            <div className="dash-empty">No strategies configured. <button className="dash-link" onClick={() => navigate('/settings')}>Go to Settings →</button></div>
          )}
        </div>
      </section>

      {/* Recent Trades */}
      <section className="dash-section">
        <div className="dash-section-header">
          <h2>Recent Trades</h2>
        </div>
        {trades.length > 0 ? (
          <div className="table-wrapper">
            <table className="trade-table-v2">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Strategy</th>
                  <th>Action</th>
                  <th>Leg</th>
                  <th>Side</th>
                  <th>Fill Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} className={t.side === 'SELL' ? 'sell-row' : 'buy-row'}>
                    <td className="trade-time">{t.timestamp?.split(' ')[1] || t.timestamp}</td>
                    <td>{t.strategy}</td>
                    <td><span className={`action-badge action-${t.action?.toLowerCase()}`}>{t.action}</span></td>
                    <td><span className={t.option_type === 'CE' ? 'ce-type' : 'pe-type'}>{t.option_type}</span> {t.strike}</td>
                    <td className={t.side === 'SELL' ? 'sell-side' : 'buy-side'}>{t.side}</td>
                    <td className="strike-cell">{t.fill_price?.toFixed(2)}</td>
                    <td><span className={`action-badge ${t.status === 'FILLED' ? 'action-enter' : 'action-exit'}`}>{t.status}{t.paper_trade ? ' (P)' : ''}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dash-empty">No trades today</div>
        )}
      </section>
    </div>
  );
}
